<script setup lang="ts">
import { storeToRefs } from 'pinia'
import type { TBadgeVariant } from '../../atom/Badge/interfaces'
import { useMenuStore } from '~/stores/menu'
import type { Maybe, Sg_Container } from '~/schema/types/generated'

interface Props {
    categories: Maybe<Sg_Container>[]
    catTitle: string
}

const { categories, catTitle } = defineProps<Props>()
const store = useMenuStore()
const { closeMobileMenu } = store
const { brandSectionData } = storeToRefs(store)
const isLibertyPopupOpen = ref(false)

const { sendMenuEvent } = useSendEvent()

function handleLinkClick(title: string, url: string, childTitle: string = '') {
    let label = `${catTitle} > ${title}`
    if (childTitle) {
        label += ` > ${childTitle}`
    }

    sendMenuEvent(label, url)
    closeMobileMenu()
}
</script>

<template>
    <PlusBlogLpAtomAccordion
        type="single"
        collapsible
        class="pt-5 md:hidden"
    >
        <div
            v-for="(cat, catIdx) in categories"
            :key="catIdx"
        >
            <PlusBlogLpAtomAccordionItem
                v-for="item in cat?.sg_items"
                :key="item?.title ?? ''"
                :value="item?.title ?? ''"
            >
                <PlusBlogLpMoleculeMenuLibertyPopup
                    v-if="item?.libertyPopup"
                    v-model="isLibertyPopupOpen"
                >
                    <template #trigger>
                        <PlusBlogLpAtomMenubarIconTitle
                            :id="item.title"
                            class="!cursor-pointer py-3 focus-visible:outline focus-visible:outline-1 focus-visible:outline-black"
                            :icon="item.icon"
                            :title="item.title"
                            @click="sendMenuEvent(`${catTitle} > ${item.title}`, item?.url ?? '')"
                        />
                    </template>
                </PlusBlogLpMoleculeMenuLibertyPopup>
                <a
                    v-else-if="item?.url"
                    :href="item?.url"
                    @click.native="handleLinkClick(item.title ?? '', item.url)"
                >
                    <PlusBlogLpAtomMenubarIconTitle
                        id="item.title"
                        class="py-5"
                        :icon="item.icon"
                        :title="item.title"
                    />
                </a>
                <template v-else>
                    <PlusBlogLpAtomAccordionTrigger
                        as="h3"
                        arrow-style="size-6"
                    >
                        <PlusBlogLpAtomMenubarIconTitle
                            :id="item?.title"
                            class="py-5"
                            :icon="item?.icon"
                            :title="item?.title"
                            @click="sendMenuEvent(`${catTitle} > ${item?.title}`)"
                        />
                    </PlusBlogLpAtomAccordionTrigger>
                    <PlusBlogLpAtomAccordionContent>
                        <ul
                            v-if="item?.sg_links?.length"
                            class="pl-9"
                        >
                            <li
                                v-for="(subcat, subcatIdx) in item.sg_links"
                                :key="subcat?.title ?? subcatIdx"
                                class="py-5"
                            >
                                <h4 class="flex items-center gap-2 font-medium">
                                    <PlusBlogLpAtomLink
                                        :link="subcat?.url ?? '#'"
                                        :value="subcat?.title ?? ''"
                                        @click.native="
                                            handleLinkClick(item?.title ?? '', subcat?.url ?? '', subcat?.title ?? '')
                                        "
                                    />
                                    <PlusBlogLpAtomBadge
                                        v-if="subcat?.badgeText"
                                        :variant="(subcat.badgeVariant as unknown as TBadgeVariant) ?? 'blue'"
                                    >
                                        {{ subcat.badgeText }}
                                    </PlusBlogLpAtomBadge>
                                </h4>
                            </li>
                        </ul>
                        <PlusBlogLpMoleculeMenuBrandSection
                            v-if="item?.brandSection && brandSectionData"
                            :brands="brandSectionData"
                            :cat-title="catTitle"
                            :sub-cat-title="item?.title ?? ''"
                            class="my-2"
                            @click="closeMobileMenu"
                        />
                    </PlusBlogLpAtomAccordionContent>
                </template>
            </PlusBlogLpAtomAccordionItem>
        </div>
    </PlusBlogLpAtomAccordion>
</template>
